/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CollectionPackagingUnitCreateRequest } from '../models/collection-packaging-unit-create-request';
import { CollectionPackagingUnitCreateResponse } from '../models/collection-packaging-unit-create-response';
import { CollectionPackagingUnitHierarchyDto } from '../models/collection-packaging-unit-hierarchy-dto';
import { CollectionPackagingUnitUpdateRequest } from '../models/collection-packaging-unit-update-request';
import { CollectionPackagingUnitUpdateResponse } from '../models/collection-packaging-unit-update-response';
import { PackagingUnitIdsByEanRequest } from '../models/packaging-unit-ids-by-ean-request';
import { PackagingUnitIdsByEanResponse } from '../models/packaging-unit-ids-by-ean-response';
import { PageProductAdminResponse } from '../models/page-product-admin-response';
import { PageProductPageResponse } from '../models/page-product-page-response';
import { PagedModelProductAdminResponse } from '../models/paged-model-product-admin-response';
import { ProductAdminResponse } from '../models/product-admin-response';
import { ProductCreateRequest } from '../models/product-create-request';
import { ProductCreateResponse } from '../models/product-create-response';
import { ProductDetailedResponse } from '../models/product-detailed-response';
import { ProductSheetResponse } from '../models/product-sheet-response';
import { ProductUpdateRequest } from '../models/product-update-request';
import { StatusUpdateRequest } from '../models/status-update-request';

/**
 * Product resource endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class ProductsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getProductById
   */
  static readonly GetProductByIdPath = '/products/{packagingUnitId}';

  /**
   * Get product by id.
   *
   * Get the product with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProductById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductById$Response(
    params: {
      packagingUnitId: string;
      editable?: boolean;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ProductDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductsService.GetProductByIdPath,
      'get'
    );
    if (params) {
      rb.path('packagingUnitId', params.packagingUnitId, {});
      rb.query('editable', params.editable, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ProductDetailedResponse>;
        })
      );
  }

  /**
   * Get product by id.
   *
   * Get the product with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getProductById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductById(
    params: {
      packagingUnitId: string;
      editable?: boolean;
    },
    context?: HttpContext
  ): Observable<ProductDetailedResponse> {
    return this.getProductById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ProductDetailedResponse>) =>
          r.body as ProductDetailedResponse
      )
    );
  }

  /**
   * Path part for operation updateProduct
   */
  static readonly UpdateProductPath = '/products/{packagingUnitId}';

  /**
   * Updates a product.
   *
   * Updates an existing product based on its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateProduct()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateProduct$Response(
    params: {
      packagingUnitId: string;
      body: ProductUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductsService.UpdateProductPath,
      'put'
    );
    if (params) {
      rb.path('packagingUnitId', params.packagingUnitId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates a product.
   *
   * Updates an existing product based on its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateProduct$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateProduct(
    params: {
      packagingUnitId: string;
      body: ProductUpdateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateProduct$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation updateCollectionPackagingUnit
   */
  static readonly UpdateCollectionPackagingUnitPath =
    '/products/{packagingUnitId}/collections/{collectionPackagingUnitId}';

  /**
   * Updates a collection packaging unit.
   *
   * Updates an existing packaging unit based on its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateCollectionPackagingUnit()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateCollectionPackagingUnit$Response(
    params: {
      packagingUnitId: string;
      collectionPackagingUnitId: string;
      body: CollectionPackagingUnitUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CollectionPackagingUnitUpdateResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductsService.UpdateCollectionPackagingUnitPath,
      'put'
    );
    if (params) {
      rb.path('packagingUnitId', params.packagingUnitId, {});
      rb.path(
        'collectionPackagingUnitId',
        params.collectionPackagingUnitId,
        {}
      );
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CollectionPackagingUnitUpdateResponse>;
        })
      );
  }

  /**
   * Updates a collection packaging unit.
   *
   * Updates an existing packaging unit based on its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateCollectionPackagingUnit$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateCollectionPackagingUnit(
    params: {
      packagingUnitId: string;
      collectionPackagingUnitId: string;
      body: CollectionPackagingUnitUpdateRequest;
    },
    context?: HttpContext
  ): Observable<CollectionPackagingUnitUpdateResponse> {
    return this.updateCollectionPackagingUnit$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<CollectionPackagingUnitUpdateResponse>) =>
          r.body as CollectionPackagingUnitUpdateResponse
      )
    );
  }

  /**
   * Path part for operation deleteCollectionPackagingUnit
   */
  static readonly DeleteCollectionPackagingUnitPath =
    '/products/{packagingUnitId}/collections/{collectionPackagingUnitId}';

  /**
   * Delete a collection packaging unit by packagingUnitId.
   *
   * Delete collection packaging unit by packagingUnitId
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteCollectionPackagingUnit()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCollectionPackagingUnit$Response(
    params: {
      packagingUnitId: string;
      collectionPackagingUnitId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductsService.DeleteCollectionPackagingUnitPath,
      'delete'
    );
    if (params) {
      rb.path('packagingUnitId', params.packagingUnitId, {});
      rb.path(
        'collectionPackagingUnitId',
        params.collectionPackagingUnitId,
        {}
      );
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Delete a collection packaging unit by packagingUnitId.
   *
   * Delete collection packaging unit by packagingUnitId
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteCollectionPackagingUnit$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCollectionPackagingUnit(
    params: {
      packagingUnitId: string;
      collectionPackagingUnitId: string;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteCollectionPackagingUnit$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation updateProductStatus
   */
  static readonly UpdateProductStatusPath = '/admin/products/status';

  /**
   * Updates the status of product by admin.
   *
   * Updates the status of product based on the provided list of product ids and a status.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateProductStatus()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateProductStatus$Response(
    params: {
      body: StatusUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductsService.UpdateProductStatusPath,
      'put'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates the status of product by admin.
   *
   * Updates the status of product based on the provided list of product ids and a status.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateProductStatus$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateProductStatus(
    params: {
      body: StatusUpdateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateProductStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getProducts
   */
  static readonly GetProductsPath = '/products';

  /**
   * Gets products.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProducts()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProducts$Response(
    params: {
      /**
       * Filter on publication status of products
       */
      status: 'PUBLISHED' | 'DRAFT' | 'REJECTED' | 'ONGOING';

      /**
       * Filter on brand
       */
      brand?: string;

      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on ean
       */
      ean?: string;

      /**
       * Filter on unit
       */
      unit?:
        | 'MILLILITERS'
        | 'CENTILITERS'
        | 'DECILITERS'
        | 'LITERS'
        | 'GRAMS'
        | 'DECAGRAMS'
        | 'KILOGRAMS'
        | 'PIECES';

      /**
       * Filter on amount
       */
      amount?: number;

      /**
       * Filter on the packingMethodSubType of the standalone packaging unit. Or filter on the root.packingMethodSubType if its a collection packaging unit.
       */
      standalonePackingMethodSubType?: string;

      /**
       * Filter based on existence of collectionPackaging
       */
      hasCollectionPackaging?: boolean;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageProductPageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductsService.GetProductsPath,
      'get'
    );
    if (params) {
      rb.query('status', params.status, {});
      rb.query('brand', params.brand, {});
      rb.query('name', params.name, {});
      rb.query('ean', params.ean, {});
      rb.query('unit', params.unit, {});
      rb.query('amount', params.amount, {});
      rb.query(
        'standalonePackingMethodSubType',
        params.standalonePackingMethodSubType,
        {}
      );
      rb.query('hasCollectionPackaging', params.hasCollectionPackaging, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageProductPageResponse>;
        })
      );
  }

  /**
   * Gets products.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getProducts$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProducts(
    params: {
      /**
       * Filter on publication status of products
       */
      status: 'PUBLISHED' | 'DRAFT' | 'REJECTED' | 'ONGOING';

      /**
       * Filter on brand
       */
      brand?: string;

      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on ean
       */
      ean?: string;

      /**
       * Filter on unit
       */
      unit?:
        | 'MILLILITERS'
        | 'CENTILITERS'
        | 'DECILITERS'
        | 'LITERS'
        | 'GRAMS'
        | 'DECAGRAMS'
        | 'KILOGRAMS'
        | 'PIECES';

      /**
       * Filter on amount
       */
      amount?: number;

      /**
       * Filter on the packingMethodSubType of the standalone packaging unit. Or filter on the root.packingMethodSubType if its a collection packaging unit.
       */
      standalonePackingMethodSubType?: string;

      /**
       * Filter based on existence of collectionPackaging
       */
      hasCollectionPackaging?: boolean;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageProductPageResponse> {
    return this.getProducts$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageProductPageResponse>) =>
          r.body as PageProductPageResponse
      )
    );
  }

  /**
   * Path part for operation createProduct
   */
  static readonly CreateProductPath = '/products';

  /**
   * Creates a new product.
   *
   * Creates a new product and returns the saved resource with its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createProduct()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createProduct$Response(
    params: {
      body: ProductCreateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ProductCreateResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductsService.CreateProductPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ProductCreateResponse>;
        })
      );
  }

  /**
   * Creates a new product.
   *
   * Creates a new product and returns the saved resource with its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createProduct$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createProduct(
    params: {
      body: ProductCreateRequest;
    },
    context?: HttpContext
  ): Observable<ProductCreateResponse> {
    return this.createProduct$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ProductCreateResponse>) =>
          r.body as ProductCreateResponse
      )
    );
  }

  /**
   * Path part for operation getCollectionPackagingUnitHierarchies
   */
  static readonly GetCollectionPackagingUnitHierarchiesPath =
    '/products/{packagingUnitId}/collections';

  /**
   * Gets all non unique collection packaging unit for the given packaging unit.
   *
   * Returns any number of list of non unique collection packaging unit. The number of lists equals the number of packaging units with the given packaging unit set as parent.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCollectionPackagingUnitHierarchies()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCollectionPackagingUnitHierarchies$Response(
    params: {
      packagingUnitId: string;
    },
    context?: HttpContext
  ): Observable<
    StrictHttpResponse<Array<Array<CollectionPackagingUnitHierarchyDto>>>
  > {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductsService.GetCollectionPackagingUnitHierarchiesPath,
      'get'
    );
    if (params) {
      rb.path('packagingUnitId', params.packagingUnitId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<
            Array<Array<CollectionPackagingUnitHierarchyDto>>
          >;
        })
      );
  }

  /**
   * Gets all non unique collection packaging unit for the given packaging unit.
   *
   * Returns any number of list of non unique collection packaging unit. The number of lists equals the number of packaging units with the given packaging unit set as parent.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCollectionPackagingUnitHierarchies$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCollectionPackagingUnitHierarchies(
    params: {
      packagingUnitId: string;
    },
    context?: HttpContext
  ): Observable<Array<Array<CollectionPackagingUnitHierarchyDto>>> {
    return this.getCollectionPackagingUnitHierarchies$Response(
      params,
      context
    ).pipe(
      map(
        (
          r: StrictHttpResponse<
            Array<Array<CollectionPackagingUnitHierarchyDto>>
          >
        ) => r.body as Array<Array<CollectionPackagingUnitHierarchyDto>>
      )
    );
  }

  /**
   * Path part for operation createCollectionPackagingUnit
   */
  static readonly CreateCollectionPackagingUnitPath =
    '/products/{packagingUnitId}/collections';

  /**
   * Creates a new collection packaging unit as a child of the given packaging unit.
   *
   * Creates a new collection packaging unit and returns the saved resource with its identifier. Collection hierarchy can be up to 4 levels deep.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createCollectionPackagingUnit()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createCollectionPackagingUnit$Response(
    params: {
      packagingUnitId: string;
      body: CollectionPackagingUnitCreateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CollectionPackagingUnitCreateResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductsService.CreateCollectionPackagingUnitPath,
      'post'
    );
    if (params) {
      rb.path('packagingUnitId', params.packagingUnitId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CollectionPackagingUnitCreateResponse>;
        })
      );
  }

  /**
   * Creates a new collection packaging unit as a child of the given packaging unit.
   *
   * Creates a new collection packaging unit and returns the saved resource with its identifier. Collection hierarchy can be up to 4 levels deep.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createCollectionPackagingUnit$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createCollectionPackagingUnit(
    params: {
      packagingUnitId: string;
      body: CollectionPackagingUnitCreateRequest;
    },
    context?: HttpContext
  ): Observable<CollectionPackagingUnitCreateResponse> {
    return this.createCollectionPackagingUnit$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<CollectionPackagingUnitCreateResponse>) =>
          r.body as CollectionPackagingUnitCreateResponse
      )
    );
  }

  /**
   * Path part for operation getIdsByEanCodes
   */
  static readonly GetIdsByEanCodesPath = '/products/ids-by-ean';

  /**
   * Gets a key-value list for ean codes and their related ids.
   *
   * The endpoint finds the product (packagingUnit) with the given ean code and pairs its id with that ean code. If ean code is not present, then the value will be null for that ean key.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getIdsByEanCodes()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getIdsByEanCodes$Response(
    params: {
      body: PackagingUnitIdsByEanRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PackagingUnitIdsByEanResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductsService.GetIdsByEanCodesPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PackagingUnitIdsByEanResponse>;
        })
      );
  }

  /**
   * Gets a key-value list for ean codes and their related ids.
   *
   * The endpoint finds the product (packagingUnit) with the given ean code and pairs its id with that ean code. If ean code is not present, then the value will be null for that ean key.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getIdsByEanCodes$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getIdsByEanCodes(
    params: {
      body: PackagingUnitIdsByEanRequest;
    },
    context?: HttpContext
  ): Observable<PackagingUnitIdsByEanResponse> {
    return this.getIdsByEanCodes$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PackagingUnitIdsByEanResponse>) =>
          r.body as PackagingUnitIdsByEanResponse
      )
    );
  }

  /**
   * Path part for operation getProductSheetById
   */
  static readonly GetProductSheetByIdPath = '/products/{packagingUnitId}/sheet';

  /**
   * Get the product sheet with the specified id.
   *
   * Product sheet with decreased subset of product information extended with prices and suppliers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProductSheetById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductSheetById$Response(
    params: {
      packagingUnitId: string;

      /**
       * Determines which logic will be used BUYING/SELLING
       */
      marketplaceSection: 'BUYING' | 'SELLING';
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ProductSheetResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductsService.GetProductSheetByIdPath,
      'get'
    );
    if (params) {
      rb.path('packagingUnitId', params.packagingUnitId, {});
      rb.query('marketplaceSection', params.marketplaceSection, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ProductSheetResponse>;
        })
      );
  }

  /**
   * Get the product sheet with the specified id.
   *
   * Product sheet with decreased subset of product information extended with prices and suppliers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getProductSheetById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductSheetById(
    params: {
      packagingUnitId: string;

      /**
       * Determines which logic will be used BUYING/SELLING
       */
      marketplaceSection: 'BUYING' | 'SELLING';
    },
    context?: HttpContext
  ): Observable<ProductSheetResponse> {
    return this.getProductSheetById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ProductSheetResponse>) =>
          r.body as ProductSheetResponse
      )
    );
  }

  /**
   * Path part for operation getProductsForAdminByMerchantNameAsHal
   */
  static readonly GetProductsForAdminByMerchantNameAsHalPath =
    '/merchants/{merchantId}/products';

  /**
   * Gets products for admin.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProductsForAdminByMerchantNameAsHal()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductsForAdminByMerchantNameAsHal$Response(
    params: {
      merchantId: string;
      brand?: string;
      name?: string;
      status?: 'PUBLISHED' | 'DRAFT' | 'REJECTED' | 'ONGOING';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagedModelProductAdminResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductsService.GetProductsForAdminByMerchantNameAsHalPath,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.query('brand', params.brand, {});
      rb.query('name', params.name, {});
      rb.query('status', params.status, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/hal+json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagedModelProductAdminResponse>;
        })
      );
  }

  /**
   * Gets products for admin.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getProductsForAdminByMerchantNameAsHal$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductsForAdminByMerchantNameAsHal(
    params: {
      merchantId: string;
      brand?: string;
      name?: string;
      status?: 'PUBLISHED' | 'DRAFT' | 'REJECTED' | 'ONGOING';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PagedModelProductAdminResponse> {
    return this.getProductsForAdminByMerchantNameAsHal$Response(
      params,
      context
    ).pipe(
      map(
        (r: StrictHttpResponse<PagedModelProductAdminResponse>) =>
          r.body as PagedModelProductAdminResponse
      )
    );
  }

  /**
   * Path part for operation getProductsForAdmin1
   */
  static readonly GetProductsForAdmin1Path = '/admin/products';

  /**
   * Gets products for admin.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProductsForAdmin1()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductsForAdmin1$Response(
    params?: {
      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on brand
       */
      brand?: string;

      /**
       * Filter on merchantName
       */
      merchantName?: string;

      /**
       * Filter on status
       */
      status?: 'PUBLISHED' | 'DRAFT' | 'REJECTED' | 'ONGOING';

      /**
       * Filter based on existence of collectionPackaging
       */
      hasCollectionPackaging?: boolean;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageProductAdminResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductsService.GetProductsForAdmin1Path,
      'get'
    );
    if (params) {
      rb.query('name', params.name, {});
      rb.query('brand', params.brand, {});
      rb.query('merchantName', params.merchantName, {});
      rb.query('status', params.status, {});
      rb.query('hasCollectionPackaging', params.hasCollectionPackaging, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageProductAdminResponse>;
        })
      );
  }

  /**
   * Gets products for admin.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getProductsForAdmin1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductsForAdmin1(
    params?: {
      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on brand
       */
      brand?: string;

      /**
       * Filter on merchantName
       */
      merchantName?: string;

      /**
       * Filter on status
       */
      status?: 'PUBLISHED' | 'DRAFT' | 'REJECTED' | 'ONGOING';

      /**
       * Filter based on existence of collectionPackaging
       */
      hasCollectionPackaging?: boolean;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageProductAdminResponse> {
    return this.getProductsForAdmin1$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageProductAdminResponse>) =>
          r.body as PageProductAdminResponse
      )
    );
  }

  /**
   * Path part for operation getProductForAdminById
   */
  static readonly GetProductForAdminByIdPath =
    '/admin/products/{packagingUnitId}';

  /**
   * Gets product for admin.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProductForAdminById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductForAdminById$Response(
    params: {
      packagingUnitId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ProductAdminResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductsService.GetProductForAdminByIdPath,
      'get'
    );
    if (params) {
      rb.path('packagingUnitId', params.packagingUnitId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ProductAdminResponse>;
        })
      );
  }

  /**
   * Gets product for admin.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getProductForAdminById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductForAdminById(
    params: {
      packagingUnitId: string;
    },
    context?: HttpContext
  ): Observable<ProductAdminResponse> {
    return this.getProductForAdminById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ProductAdminResponse>) =>
          r.body as ProductAdminResponse
      )
    );
  }
}
